import React, { useState, useEffect, useContext } from 'react';
import Axios from 'axios';

import { UserContext, SnackbarContext } from './userContext';

/* ─────────── Inline icons ─────────── */

const Icon = {
  Person: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  Phone: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72a2 2 0 0 1 1.72 2z" />
    </svg>
  ),
  Mail: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <path d="m22 6-10 7L2 6" />
    </svg>
  ),
  ArrowRight: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  ),
  Close: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  ),
  Spark: ({ className }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
};

/* ─────────── Field ─────────── */

function Field({ label, icon, type = 'text', value, onChange, error, errorMsg, helper, required, autoComplete, inputMode, maxLength, placeholder }) {
  const [focused, setFocused] = useState(false);
  const showError = !!error;
  return (
    <div className="w-full">
      <label className="mb-1.5 block text-[10.5px] font-bold uppercase tracking-[0.14em] text-fg-tertiary">
        {label}
        {required && <span className="ml-1 text-amber">*</span>}
      </label>
      <div
        className={`
          relative flex h-11 items-center gap-2 rounded-lg px-3
          bg-surface-overlay/70
          border
          transition-[border-color,background-color,box-shadow] duration-200 ease-out-soft
          ${showError
            ? 'border-red-400 shadow-[0_0_0_3px_rgba(248,113,113,0.16)]'
            : focused
              ? 'border-amber bg-surface-elevated shadow-[0_0_0_3px_rgba(251,191,36,0.18)]'
              : 'border-border-divider hover:border-border-strong'}
        `}
      >
        {icon && (
          <span className={`shrink-0 transition-colors duration-200 ${showError ? 'text-red-400' : focused ? 'text-amber' : 'text-fg-tertiary'}`}>
            {icon}
          </span>
        )}
        <input
          type={type}
          value={value}
          onChange={onChange}
          required={required}
          autoComplete={autoComplete}
          inputMode={inputMode}
          maxLength={maxLength}
          placeholder={placeholder}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          className="min-w-0 flex-1 bg-transparent text-[13.5px] text-fg-primary placeholder:text-fg-tertiary outline-none border-0 p-0"
        />
      </div>
      <div className={`mt-1 min-h-[14px] text-[11px] leading-tight ${showError ? 'text-red-400' : 'text-fg-tertiary'}`}>
        {showError && errorMsg ? errorMsg : (helper || ' ')}
      </div>
    </div>
  );
}

/* ─────────── Modal ─────────── */

function Modal({ open, onClose, children }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4">
      <div
        aria-hidden
        className="absolute inset-0 bg-black/72 backdrop-blur-sm animate-[fadeIn_180ms_ease-out]"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        className="
          relative w-full max-w-md rounded-2xl
          bg-surface-elevated border border-border-subtle
          p-5 sm:p-6
          animate-[fadeIn_180ms_ease-out]
          shadow-[0_24px_64px_-12px_rgba(0,0,0,0.8),0_0_64px_-8px_rgba(251,191,36,0.18)]
        "
      >
        {children}
      </div>
    </div>
  );
}

/* ═══════════════════ ContactForm ═══════════════════ */

export const ContactForm = (props) => {
  const { user, setUser } = useContext(UserContext);
  const { setSnack } = useContext(SnackbarContext);

  const [name, setName] = useState('');
  const [data, setData] = useState(null);
  const [url, setUrl] = useState('');
  const [mob, setMob] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState(0);

  useEffect(() => {
    setData(props.data);
    setUrl(props.url);
    if (user) {
      if (user.name) setName(user.name);
      if (user.user_mobile) setMob(user.user_mobile);
      if (user.user_email) setEmail(user.user_email);
    }
  }, [props.data, props.url, user]);

  const handleRes = (res) => {
    const { status, msg } = res.data;
    if (status === 1) {
      props.hideload();
      setSnack({ message: msg, type: 'success', open: true });
    } else if (status === 2) {
      props.hideload();
      setSnack({ message: msg, type: 'error', open: true });
    } else if (status === 3) {
      props.hideload();
      setSnack({ message: msg, type: 'warning', open: true, direction: 'left' });
    } else if (status === 4) {
      props.hideload();
      setUser(null);
      localStorage.removeItem('estkn');
      setSnack({ message: msg, type: 'warning', open: true, direction: 'left' });
    }
  };

  const contactEngWf = () => {
    props.closeform();
    props.showload();
    const formData = { uid: user.user_id, catid: user.category_ids, name, mob, email };
    if (data.category_ids === '7') formData.eid = data.user_id;
    else if (data.category_ids === '6') formData.wid = data.user_id;
    const urlll = data.category_ids === '7' ? '/new_apis/contactnoweng' : '/new_apis/contactnowwf';
    Axios.post(urlll, formData, { headers: { tkn: user.token } }).then(handleRes);
  };

  const contactProd = () => {
    props.closeform();
    props.showload();
    const formData = {
      uid: user.user_id,
      catid: user.category_ids,
      vid: data.user_ids,
      pid: data.product_id,
      pname: data.product_name,
      name, mob, email,
    };
    Axios.post('/new_apis/contactnowprod', formData, { headers: { tkn: user.token } }).then(handleRes);
  };

  const contactRealEstate = () => {
    props.closeform();
    props.showload();
    const formData = { uid: user.user_id, name, mob, email, reid: data.re_id };
    Axios.post('/new_apis/contactnowrealestate', formData, { headers: { tkn: user.token } }).then(handleRes);
  };

  const applyJob = () => {
    props.closeform();
    props.showload();
    const formData = { uid: user.user_id, name, mob, email, jid: data.job_id };
    Axios.post('/new_apis/apply-job', formData, { headers: { tkn: user.token } }).then(handleRes);
  };

  const submit = (e) => {
    if (e) e.preventDefault();
    setError(0);
    if (name === '') {
      setError(1);
      setSnack({ message: 'Please enter your name.', type: 'error', open: true });
      return;
    }
    if (mob.length !== 10) {
      setError(2);
      setSnack({ message: 'Enter a valid 10-digit mobile number.', type: 'error', open: true });
      return;
    }
    if (url === 'prod') contactProd();
    else if (url === 'engwf') contactEngWf();
    else if (url === 'realestate') contactRealEstate();
    else if (url === 'careers') applyJob();
  };

  const isApply = url === 'careers';
  const title = isApply ? 'Apply for this job' : 'Get in touch';
  const desc = isApply
    ? 'Confirm your details to submit your application.'
    : 'Confirm your contact details - they\'ll be sent to the listing owner.';
  const submitLabel = isApply ? 'Submit application' : 'Send contact request';
  const kicker = isApply ? 'Apply' : 'Contact';

  // Show a small product/job context chip if data has a name
  const contextLabel =
    data?.product_name ||
    data?.job_title ||
    null;

  return (
    <Modal open={!!props.open} onClose={props.closeform}>
      <button
        type="button"
        onClick={props.closeform}
        aria-label="Close"
        className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-md text-fg-tertiary hover:bg-surface-overlay hover:text-fg-primary transition-colors"
      >
        <Icon.Close className="h-4 w-4" />
      </button>

      {/* Kicker */}
      <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-amber/24 bg-amber/10 px-2.5 py-1">
        <span aria-hidden className="inline-flex h-1.5 w-1.5 rounded-full bg-amber" style={{ boxShadow: '0 0 10px rgba(251,191,36,0.7)' }} />
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber">{kicker}</span>
      </div>

      {/* Title + description */}
      <h3 className="text-[19px] sm:text-[20px] font-bold tracking-[-0.02em] text-fg-primary leading-tight">
        {title}
      </h3>
      <p className="mt-1.5 text-[13px] leading-relaxed text-fg-secondary">{desc}</p>

      {/* Optional context chip */}
      {contextLabel && (
        <div className="mt-3 inline-flex max-w-full items-center gap-2 rounded-md border border-border-subtle bg-surface-overlay/60 px-2.5 py-1.5">
          <span aria-hidden className="text-amber shrink-0">
            <Icon.Spark className="h-3.5 w-3.5" />
          </span>
          <span className="truncate text-[12px] font-medium text-fg-secondary">{contextLabel}</span>
        </div>
      )}

      {/* Form */}
      <form onSubmit={submit} noValidate className="mt-5 flex flex-col gap-1">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field
            label="Name"
            required
            icon={<Icon.Person className="h-[16px] w-[16px]" />}
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={error === 1}
            errorMsg="Please enter your name."
            helper=" "
            autoComplete="name"
            placeholder="Jane Doe"
          />
          <Field
            label="Mobile"
            required
            type="tel"
            icon={<Icon.Phone className="h-[16px] w-[16px]" />}
            value={mob}
            onChange={(e) => setMob(e.target.value.replace(/\D/g, '').slice(0, 10))}
            error={error === 2}
            errorMsg="Enter a valid 10-digit mobile number."
            helper=" "
            inputMode="numeric"
            maxLength={10}
            autoComplete="tel"
            placeholder="9876543210"
          />
        </div>
        <Field
          label="Email"
          type="email"
          icon={<Icon.Mail className="h-[16px] w-[16px]" />}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          helper="Optional"
          autoComplete="email"
          placeholder="you@email.com"
        />

        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={props.closeform}
            className="
              inline-flex h-10 items-center justify-center rounded-lg px-4
              text-[12.5px] font-semibold text-fg-secondary
              hover:bg-surface-overlay hover:text-fg-primary
              transition-colors duration-200
            "
          >
            Cancel
          </button>
          <button
            type="submit"
            className="
              group inline-flex h-10 items-center justify-center gap-1.5 rounded-lg px-5
              text-[12px] font-bold uppercase tracking-[0.1em]
              bg-amber text-ink
              hover:bg-amber-light hover:shadow-[0_0_0_3px_rgba(251,191,36,0.25),0_8px_22px_-8px_rgba(251,191,36,0.55)]
              active:scale-[0.98]
              transition-all duration-200 ease-out-soft
            "
          >
            {submitLabel}
            <Icon.ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
          </button>
        </div>
      </form>
    </Modal>
  );
};
